import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Snackbar, Alert, IconButton, Grid, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../api';

export default function Expenses() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    category: '',
    description: '',
    recipient: '',
    payment_method: '',
    note: '',
  });
  const [editId, setEditId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addCat, setAddCat] = useState(false);
  const [newCat, setNewCat] = useState('');

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
  }, []);

  // Handle auto-opening modals from Dashboard state
  useEffect(() => {
    if (location.state?.action === 'create') {
      handleOpen();
      // Clear state 
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  function fetchExpenses() {
    setLoading(true);
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}expenses`, { headers: token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' } })
      .then(res => res.json())
      .then(data => setExpenses(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }
  
  function fetchCategories() {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}expense-categories`, { headers: token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' } })
      .then(res => res.json())
      .then(data => setCategories(Array.isArray(data) ? data : []));
  }

  function handleOpen() {
    setForm({
      amount: '',
      date: new Date().toISOString().slice(0, 10),
      category: '',
      description: '',
      recipient: '',
      payment_method: '',
      note: '',
    });
    setEditId(null);
    setAddCat(false);
    setNewCat('');
    setOpen(true);
  }
  
  function handleEdit(exp) {
    setForm({ ...exp });
    setEditId(exp.id);
    setAddCat(false);
    setNewCat('');
    setOpen(true);
  }
  
  function handleClose() { 
    setOpen(false); 
    setEditId(null); 
    setAddCat(false); 
    setNewCat(''); 
  }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  function handleCategoryChange(e) {
    if (e.target.value === '__add__') {
      setAddCat(true);
      setForm(f => ({ ...f, category: '' }));
    } else {
      setForm(f => ({ ...f, category: e.target.value }));
      setAddCat(false);
      setNewCat('');
    }
  }

  function handleAddCategory() {
    if (!newCat.trim()) return;
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}expense-categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      body: JSON.stringify({ name: newCat, type: 'custom' })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to add category');
        return res.json();
      })
      .then(cat => {
        setCategories(cats => [...cats, cat]);
        setForm(f => ({ ...f, category: cat.name }));
        setAddCat(false);
        setNewCat('');
        setSnackbar({ open: true, message: 'Category added!', severity: 'success' });
      })
      .catch(err => setSnackbar({ open: true, message: err.message, severity: 'error' }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.amount || !form.date || !form.category) {
      setSnackbar({ open: true, message: 'Amount, Date and Category are required!', severity: 'error' });
      return;
    }
    const method = editId ? 'PUT' : 'POST';
    const url = editId ? `${API_BASE_URL}expenses/${editId}` : `${API_BASE_URL}expenses`;
    const token = localStorage.getItem('token');
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      body: JSON.stringify(form)
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to save expense');
        return res.json();
      })
      .then(() => {
        setSnackbar({ open: true, message: editId ? 'Expense updated!' : 'Expense added!', severity: 'success' });
        setOpen(false);
        setEditId(null);
        fetchExpenses();
      })
      .catch(err => setSnackbar({ open: true, message: err.message, severity: 'error' }));
  }

  function handleDelete(id) {
    if (!window.confirm('Delete this expense?')) return;
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}expenses/${id}`, { method: 'DELETE', headers: token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' } })
      .then(res => {
        if (!res.ok) throw new Error('Failed to delete expense');
        return res.json();
      })
      .then(() => {
        setSnackbar({ open: true, message: 'Expense deleted!', severity: 'success' });
        fetchExpenses();
      })
      .catch(err => setSnackbar({ open: true, message: err.message, severity: 'error' }));
  }

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography>Loading expenses...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 3,
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.35rem', sm: '2rem' }, minWidth: 0 }}>
            Expenses Management
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={handleOpen}
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
              },
              fontWeight: 600,
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
              flexShrink: 0,
            }}
          >
            Add New Expense
          </Button>
        </Box>
        
        <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Payment Method</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.map(exp => (
                <TableRow key={exp.id}>
                  <TableCell>{exp.date}</TableCell>
                  <TableCell>£{Number(exp.amount).toFixed(2)}</TableCell>
                  <TableCell>{exp.category}</TableCell>
                  <TableCell>{exp.description}</TableCell>
                  <TableCell>{exp.recipient}</TableCell>
                  <TableCell>{exp.payment_method}</TableCell>
                  <TableCell>{exp.note}</TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleEdit(exp)}><EditIcon /></IconButton>
                    <IconButton color="error" onClick={() => handleDelete(exp.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Improved Expense Form Dialog */}
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0', fontSize: '1.5rem' }}>
            {editId ? 'Edit Expense' : 'Add New Expense'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent sx={{ pt: 3 }}>
              {/* Basic Information Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 700, fontSize: '1.5rem' }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      name="amount" 
                      label="Amount (£)" 
                      type="number" 
                      value={form.amount} 
                      onChange={handleChange} 
                      required 
                      placeholder="0.00"
                      inputProps={{ step: "0.01", min: "0" }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      name="date" 
                      label="Date" 
                      type="date" 
                      value={form.date} 
                      onChange={handleChange} 
                      required 
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Category</InputLabel>
                      <Select name="category" value={form.category} label="Category" onChange={handleCategoryChange}>
                        {categories.map(cat => (
                          <MenuItem key={cat.name} value={cat.name}>{cat.name}</MenuItem>
                        ))}
                        <MenuItem value="__add__">+ Add new category...</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {addCat && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField 
                          label="New Category" 
                          value={newCat} 
                          onChange={e => setNewCat(e.target.value)} 
                          sx={{ flex: 1 }}
                          placeholder="Enter new category name"
                        />
                        <Button onClick={handleAddCategory} variant="contained" size="small">
                          Add
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
              
              {/* Additional Details Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 700, fontSize: '1.5rem' }}>
                  Additional Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField 
                      name="description" 
                      label="Description" 
                      value={form.description} 
                      onChange={handleChange} 
                      placeholder="Describe the expense"
                      multiline
                      rows={2}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      name="recipient" 
                      label="Recipient" 
                      value={form.recipient} 
                      onChange={handleChange} 
                      placeholder="Who received the payment"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      name="payment_method" 
                      label="Payment Method" 
                      value={form.payment_method} 
                      onChange={handleChange} 
                      placeholder="Cash, Card, Bank Transfer"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={handleClose} sx={{ color: '#64748b', fontWeight: 500 }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" sx={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                },
                fontWeight: 600,
                textTransform: 'none',
              }}>
                {editId ? 'Update Expense' : 'Add Expense'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
} 